import { Search, MapPin, Clock, Briefcase, ArrowRight, Zap } from 'lucide-react';

export default function Careers() {
    const jobs = [
        {
            title: "Senior Full Stack Developer",
            department: "Engineering",
            location: "Wyoming / Remote",
            type: "Full-time",
            salary: "$100k - $400k"
        },
        {
            title: "Career Coach & Mentor",
            department: "Talent",
            location: "Remote",
            type: "Part-time",
            salary: "$70k - $300k"
        },
        {
            title: "UI/UX Designer",
            department: "Design",
            location: "Hybrid",
            type: "Full-time",
            salary: "$50k - $100k"
        },
        {
            title: "Technical Content Writer",
            department: "Marketing",
            location: "Remote",
            type: "Contract",
            salary: "$70k - $150k"
        },
        {
            title: "Cloud Infrastructure Engineer (AWS/DevOps)",
            department: "Engineering",
            location: "Remote",
            type: "Contract",
            salary: "$90k - $180k"
        },
        {
            title: "React Native Developer",
            department: "Engineering",
            location: "Remote",
            type: "Contract",
            salary: "$80k - $140k"
        },
        {
            title: "Cyber Security Analyst",
            department: "Security",
            location: "Hybrid",
            type: "Contract",
            salary: "$100k - $160k"
        },
        {
            title: "Data Engineer (Snowflake & dbt)",
            department: "Data & Analytics",
            location: "Remote",
            type: "Contract",
            salary: "$95k - $170k"
        }
    ];

    return (
        <div className="relative min-h-screen bg-brand-light text-slate-900 overflow-hidden">

            {/* GLOBAL BG */}
            <div className="absolute inset-0 bg-brand-radial -z-10"></div>

            {/* HERO */}
            <section className="pt-32 pb-16 px-6 text-center">
                <div className="max-w-4xl mx-auto">

                    <h1 className="text-5xl md:text-6xl font-extrabold mb-6 tracking-tight">
                        Help us bridge the <br />
                        <span className="bg-brand-gradient bg-clip-text text-transparent">
                            opportunity gap
                        </span>
                    </h1>

                    <p className="text-xl text-slate-600 leading-relaxed mb-10">
                        Join a remote-first team building the future of recruitment.
                    </p>

                    {/* SEARCH */}
                    <div className="max-w-2xl mx-auto relative group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-primary" size={20} />

                        <input
                            type="text"
                            placeholder="Search roles (e.g. React Developer)"
                            className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/80 backdrop-blur-xl border border-white/40 focus:outline-none focus:ring-4 focus:ring-brand-primary/30 transition-all shadow-xl"
                        />
                    </div>
                </div>
            </section>

            {/* PERKS */}
            <section className="py-20 px-6 max-w-7xl mx-auto">
                <div className="grid md:grid-cols-3 gap-10">

                    <div className="text-center">
                        <div className="w-14 h-14 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-4">
                            <Zap className="text-brand-primary" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Remote-First</h3>
                        <p className="text-slate-600">Work from anywhere in India.</p>
                    </div>

                    <div className="text-center">
                        <div className="w-14 h-14 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Clock className="text-brand-accent" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Flexible Hours</h3>
                        <p className="text-slate-600">Async-first work culture.</p>
                    </div>

                    <div className="text-center">
                        <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Briefcase className="text-brand-dark" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Learning Stipend</h3>
                        <p className="text-slate-600">We fund your growth.</p>
                    </div>

                </div>
            </section>

            {/* JOB LIST */}
            <section className="py-16 px-6">
                <div className="max-w-5xl mx-auto">

                    <div className="flex justify-between items-end mb-10">
                        <div>
                            <h2 className="text-3xl font-bold mb-2">Open Roles</h2>
                            <p className="text-slate-600">Join our growing team</p>
                        </div>

                        <span className="bg-white/80 backdrop-blur px-4 py-2 rounded-lg border border-white/40 text-sm font-semibold shadow-sm">
                            {jobs.length} Positions
                        </span>
                    </div>

                    <div className="space-y-4">
                        {jobs.map((job, i) => (
                            <div
                                key={i}
                                className="group bg-white/70 backdrop-blur-xl p-6 rounded-2xl border border-white/40 shadow-sm hover:shadow-xl hover:border-brand-primary/30 transition-all cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4"
                            >
                                <div>
                                    <h3 className="text-xl font-bold group-hover:text-brand-primary transition-colors">
                                        {job.title}
                                    </h3>

                                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                                        <div className="flex items-center gap-1">
                                            <MapPin size={14} /> {job.location}
                                        </div>

                                        <div className="flex items-center gap-1">
                                            <Briefcase size={14} /> {job.department}
                                        </div>

                                        <div className="font-medium text-brand-primary">
                                            {job.salary}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <span className="px-3 py-1 bg-brand-light rounded-md text-xs font-bold uppercase tracking-wider text-brand-dark">
                                        {job.type}
                                    </span>

                                    <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center group-hover:bg-brand-primary group-hover:text-white transition-all">
                                        <ArrowRight size={20} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 text-center">
                        <p className="text-slate-500 italic">
                            Don't see a role? Send your resume to
                            <span className="text-brand-primary font-bold ml-1">
                                careers@geniusbridge.com
                            </span>
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}